import { Fragment, FunctionComponent, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { EntrepriseGroupeCard } from '../../../components/EntrepriseGroupeCard';
import { LoadingAbsolute } from '../../../components/LoadingAbsolute';
import { Config } from '../../../config/config';
import { useApi } from '../../../providers/ApiProvider';
import { GoodApiResponse, ErrorApiResponse } from '../../../types/api';
import { UserInfo } from '../../../types/auth';
import { Groupe } from '../../../types/SalariesRessources';
import { CheckCircleIcon, ClockIcon, PlayCircleIcon } from '@heroicons/react/24/outline';
import { handleError } from '../../../utils/ErrorHandler';
import { Tab, TabPanel, Tabs, TabsBody, TabsHeader } from '../../../components/Onglets';

/**
 * Ce coposant affiche la liste des formations des salariés de l'entreprise.
 */
export const EntrepriseFormationsScreen: FunctionComponent = () => {
  /**
   * On stocke si on est en train d'utiliser l'API ou non.
   */
  const [apiIsLoading, setApiIsLoading] = useState(false);

  /**
   * État local qui gère les sessions de formations
   */
  const [groupes, setGroupes] = useState<(Groupe & { users: UserInfo[] })[] | null>();

  /**
   * On à besoin d'initialiser le client d'API à l'aide d'un hook personalisé.
   */
  const client = useApi();

  /**
   * Date du jour.
   *
   * @constant
   */
  const currentDate = new Date();

  /**
   * Permet de recharger les données au chargement de page
   */
  useEffect(() => {
    handleIndex();
  }, [client]);

  /**
   * Cette méthode va chercher les informations dans l'API pour afficher les sessions.
   */
  const handleIndex = () => {
    setApiIsLoading(true);
    client
      .get<GoodApiResponse<(Groupe & { user: UserInfo })[]>>('entreprise/salaries/groupes')
      .then((response) => {
        let newGroupes: (Groupe & { users: UserInfo[] })[] = [];

        response.data.data.forEach((groupe) => {
          const newGroupesIndex = newGroupes.findIndex((newGroupe) => newGroupe.token === groupe.token);
          if (newGroupesIndex === -1) {
            newGroupes = [...newGroupes, { ...groupe, users: [groupe.user] }];
          } else {
            newGroupes = [
              ...newGroupes.slice(0, newGroupesIndex),
              { ...newGroupes[newGroupesIndex], users: [...newGroupes[newGroupesIndex].users, groupe.user] },
              ...newGroupes.slice(newGroupesIndex + 1),
            ];
          }
        });
        setGroupes([...newGroupes]);
      })
      .catch((error: ErrorApiResponse<Groupe>) => {
        handleError(error);
      })
      .finally(() => {
        setApiIsLoading(false);
      });
  };

  if (groupes === null) {
    return <Fragment>Chargement...</Fragment>;
  }

  /**
   * Contenu des onglets.
   *
   * @constant
   */
  const data = [
    {
      label: 'Toutes',
      value: 'toutes',
      icone: <CheckCircleIcon className='w-5 h-5 flex-none' />,
    },
    {
      label: 'En cours',
      value: 'encours',
      icone: <PlayCircleIcon className='w-5 h-5 flex-none' />,
    },
    {
      label: 'A venir',
      value: 'avenir',
      icone: <ClockIcon className='w-5 h-5 flex-none' />,
    },
    {
      label: 'Terminées',
      value: 'terminees',
      icone: <CheckCircleIcon className='w-5 h-5 flex-none' />,
    },
  ] as const;

  const g_encours = groupes
    ? groupes.filter((groupe) => groupe.date_debut && groupe.date_fin && new Date(groupe.date_debut) <= currentDate && currentDate <= new Date(groupe.date_fin))
    : null;

  const g_avenir = groupes ? groupes.filter((groupe) => groupe.date_debut && currentDate < new Date(groupe.date_debut)) : null;

  const g_termines = groupes ? groupes.filter((groupe) => groupe.date_fin && new Date(groupe.date_fin) < currentDate) : null;

  return (
    <Fragment>
      <Helmet>
        <title>Mes formations - {Config.app_label}</title>
      </Helmet>

      <div className='px-4 md:px-5 py-4 md:py-4 bg-gray-100 rounded-t-lg'>
        <div className='flex flex-col lg:flex-row items-center justify-between'>
          <h1 className='text-base sm:text-lg md:text-xl poppins-bold leading-normal text-secondary-1'>Formations salariés</h1>
        </div>
      </div>
      <Tabs value='encours'>
        <TabsHeader>
          {data.map(({ label, value, icone }) => (
            <Tab key={value} value={value}>
              <div className='flex flex-row gap-2 items-center'>
                {icone}
                <span>{label}</span>
              </div>
            </Tab>
          ))}
        </TabsHeader>

        <TabsBody>
          <TabPanel value='toutes'>
            <div className='bg-white p-5 rounded-b-lg'>
              {groupes && groupes.length > 0
                ? groupes.map((groupe) => <EntrepriseGroupeCard key={groupe.token} groupe={groupe} />)
                : "Il n'y a pas de formations salariés pour l'instant"}
            </div>
          </TabPanel>

          <TabPanel value='encours'>
            <div className='bg-white p-5 rounded-b-lg'>
              {g_encours && g_encours.length > 0
                ? g_encours.map((groupe) => <EntrepriseGroupeCard key={groupe.token} groupe={groupe} />)
                : "Il n'y a pas de formations en cours pour l'instant"}
            </div>
          </TabPanel>

          <TabPanel value='avenir'>
            <div className='bg-white p-5 rounded-b-lg'>
              {g_avenir && g_avenir.length > 0
                ? g_avenir.map((groupe) => <EntrepriseGroupeCard key={groupe.token} groupe={groupe} />)
                : "Il n'y a pas de formations à venir pour l'instant"}
            </div>
          </TabPanel>

          <TabPanel value='terminees'>
            <div className='bg-white p-5 rounded-b-lg'>
              {g_termines && g_termines.length > 0
                ? g_termines.map((groupe) => <EntrepriseGroupeCard key={groupe.token} groupe={groupe} />)
                : "Il n'y a pas de formations terminées pour l'instant"}
            </div>
          </TabPanel>
        </TabsBody>
      </Tabs>
      {apiIsLoading ? <LoadingAbsolute /> : ''}
    </Fragment>
  );
};
